import { createFileRoute, Link } from '@tanstack/react-router'
import { contentWrapper } from '../../../../../styles/contentWrapper';
import { pageTitle, sectionWrapper, titleWrapper } from '../../../../../styles/sectionStyles';
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import { css } from '../../../../../../styled-system/css';
import { inputLabel, inputWrapper } from '../../../../../styles/inputStyles';
import { NotificationHistoryPreview } from '../../../../../components/notification-preview';
import { NotificationContentForm } from '../../../../../components/notification-content-form';
import { LoadingOverlay } from '../../../../../components/loading-overlay';
import { useGetUser, useGetNotificationHistories } from '../../../../../libs/admin/hooks';
import { formatTimestampJaJst } from '../../../../../libs/utils/format-timestamp';
import type { ReactNode } from 'react';
import type z from 'zod';
import type { buttonTemplateSchema } from '../../../../../libs/admin/schemas/create-notification';

export const Route = createFileRoute(
  '/admin/users/$subscriptionOrUserId/notifications/$id',
)({
  component: RouteComponent,
})

function RouteComponent() {
  return <Layout />;
}

const Layout = () => {
  const { subscriptionOrUserId, id } = Route.useParams();

  // Fetch user first to get userId
  const { data: userData, isLoading: isUserLoading } = useGetUser({ subscriptionOrUserId });
  const userId = userData?.user?.id;

  // Then fetch notification histories using userId
  const { data: notificationHistory, isLoading: isNotificationLoading } = useGetNotificationHistories({ userId });

  // Find the specific notification by id
  const notification = notificationHistory?.find(n => n.id === id);

  const isLoading = isUserLoading || isNotificationLoading;

  // Parse buttonTemplate if it's a string
  const buttonTemplate = notification?.original?.buttonTemplate
    ? JSON.parse(notification.original.buttonTemplate)
    : null;

  if (isLoading) {
    return (
      <div className={contentWrapper()}>
        <LoadingOverlay />
      </div>
    );
  }

  if (!notification) {
    return (
      <div className={contentWrapper()}>
        <Title />
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}>
          <BreadCrumbs />
          <div className={css({
            padding: '32px',
            textAlign: 'center',
            color: 'text.secondary',
          })}>
            通知が見つかりません
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={contentWrapper()}>
      <Title />
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumbs />
        <div className={css({
          display: 'flex',
          gap: '32px',
        })}>
          <NotificationHistoryPreview
            original={notification.original}
            translations={notification.translations}
          >
            {(languageSelector) => (
              <Content
                notification={notification}
                buttonTemplate={buttonTemplate}
                languageSelector={languageSelector}
              />
            )}
          </NotificationHistoryPreview>
        </div>
      </div>
    </div>
  )
}

const Title = () => {
  const { subscriptionOrUserId } = Route.useParams();
  return (
    <div className={titleWrapper()}>
      <h1 className={pageTitle()}>
        <Link to='/admin/users/$subscriptionOrUserId' params={{ subscriptionOrUserId }}>
          <ArrowBack />
        </Link>
        プッシュ通知詳細
      </h1>
    </div>
  );
}

const BreadCrumbs = () => {
  const { subscriptionOrUserId } = Route.useParams();
  const { data: userData, isLoading } = useGetUser({ subscriptionOrUserId });

  if (isLoading) {
    return null;
  }

  if (!userData?.user) {
    return null;
  }

  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link to="/admin/users">
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          利用者検索
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <Link to={"/admin/users/$subscriptionOrUserId"} params={{ subscriptionOrUserId }}>
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>{userData.user.name || "--"}</span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>プッシュ通知詳細</span>
    </div>
  );
};

type ButtonTemplate = z.infer<typeof buttonTemplateSchema>;

const Content = ({
  notification,
  buttonTemplate,
  languageSelector,
}: {
  notification: NonNullable<ReturnType<typeof useGetNotificationHistories>['data']>[0];
  buttonTemplate: ButtonTemplate | null;
  languageSelector: ReactNode;
}) => {
  const { subscriptionOrUserId } = Route.useParams();
  const { data: userData, isLoading: isUserLoading } = useGetUser({ subscriptionOrUserId });

  if (isUserLoading) {
    return <LoadingOverlay />;
  }

  if (!userData?.user) {
    return <div>ユーザーが見つかりません</div>;
  }

  return (
    <div className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      width: '100%',
    })}>
      <div className={css(
        sectionWrapper.raw(),
        {
          backgroundColor: 'transparent',
        }
      )}>
        <div className={inputWrapper()}>
          <h2 className={inputLabel()}>送信対象</h2>
          <p>{userData.user.name}</p>
        </div>
        <div className={inputWrapper()}>
          <h2 className={inputLabel()}>送信日時</h2>
          <p>{notification.createdAt ? formatTimestampJaJst(notification.createdAt) : "--"}</p>
        </div>
      </div>
      <div className={sectionWrapper()}>
        {/* 通知内容 - All fields are disabled */}
        <NotificationContentForm
          titleTemplateValue={notification.original?.titleTemplate ?? ''}
          onTitleTemplateChange={() => {}} // No-op since fields are disabled
          isTitleTemplateValid={true}
          titleTemplateValidationMessage={undefined}

          messageTemplateValue={notification.original?.messageTemplate ?? ''}
          onMessageTemplateChange={() => {}} // No-op since fields are disabled
          isMessageTemplateValid={true}
          messageTemplateValidationMessage={undefined}

          buttonVariantValue={buttonTemplate?.variant ?? 'none'}
          onButtonVariantChange={() => {}} // No-op since fields are disabled
          isButtonVariantValid={true}

          buttonTextValue={buttonTemplate?.text ?? ''}
          onButtonTextChange={() => {}} // No-op since fields are disabled
          isButtonTextValid={true}
          buttonTextValidationMessage={undefined}

          buttonServiceIdValue={buttonTemplate?.serviceId ?? ''}
          onButtonServiceIdChange={() => {}} // No-op since fields are disabled

          buttonHrefValue={buttonTemplate?.href ?? ''}
          onButtonHrefChange={() => {}} // No-op since fields are disabled
          isButtonHrefValid={true}
          buttonHrefValidationMessage={undefined}

          serviceUrlTemplateValue={notification.original?.serviceUrlTemplate ?? ''}
          onServiceUrlTemplateChange={() => {}} // No-op since fields are disabled
          isServiceUrlTemplateValid={true}
          serviceUrlTemplateValidationMessage={undefined}

          disabled={true} // All fields are disabled
        />
      </div>
      {languageSelector}
    </div>
  )
}
